// src/pages/vendor/FarmerVendorDashboard.js
import { useEffect, useMemo, useState, useLayoutEffect, useCallback } from "react";
import { auth, db, storage } from "../../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// ✅ MUI Admin UI (NEW)
import {
  AppBar,
  Toolbar,
  Drawer,
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Grid,
  Divider,
  Stack,
  CircularProgress,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";

/* =========================
   MASTER / LABELS (BEST)
========================= */

const cx = (...xs) => xs.filter(Boolean).join(" ");

/* -------------------------
   COMPONENT LABELS
------------------------- */
const COMPONENT_LABELS = {
  PLANTING: "Planting Material",
  MACH_BANK: "Machinery Bank / Tractor",
  IRRIGATION: "Drip / Sprinkler / Talavdi",
  INM_IPM: "INM / IPM Kits",
  GAP_SOIL: "Soil Testing Lab / GAP",
  PRECISION: "Precision Tech (Drone / IoT)",
  INNOVATIVE: "Innovative Tools",
};

/* -------------------------
   Legacy → Canonical
------------------------- */
const CANONICAL_CATEGORY_MAP = {
  MACH_BANK: "MACH_BANK",
  MACHINERY: "MACH_BANK",
  IRRIGATION: "IRRIGATION",
  INM_IPM: "INM_IPM",
  GAP_SOIL: "GAP_SOIL",
  PRECISION: "PRECISION",
  INNOVATIVE: "INNOVATIVE",
  TOOLS: "INNOVATIVE",
};
const toComponentId = (raw) => CANONICAL_CATEGORY_MAP[raw] || raw || "";

/* -------------------------
   Tractor brands master
------------------------- */
const TRACTOR_BRANDS_MASTER = [
  "John Deere",
  "Mahindra",
  "Swaraj",
  "Sonalika",
  "New Holland",
  "TAFE / Massey Ferguson",
  "Eicher",
  "Kubota",
  "Powertrac",
  "Farmtrac",
];

/* =========================
   MACHINERY MASTER (SMAM-like)
========================= */

// id helper
const toId = (label) =>
  String(label || "")
    .toUpperCase()
    .replace(/&/g, "AND")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Machinery groups (top-level)
const MACH_GROUPS = [
  { id: "TRACTORS", label: "Tractors" },
  { id: "POWER_TILLERS", label: "Power Tillers" },
  { id: "COMBINE_HARVESTERS", label: "Combine Harvesters" },
  { id: "KISAN_DRONE", label: "Kisan Drone / Spraying Drone" },
  { id: "RICE_TRANSPLANTERS", label: "Rice / Paddy Transplanters" },
  { id: "SELF_PROPELLED_AGRI", label: "Self-propelled Agri / Hort Machinery" },
  { id: "LAND_DEV_TILLAGE", label: "Land Development & Tillage Implements" },
  { id: "SOWING_PLANTING", label: "Sowing / Planting / Seeding" },
  { id: "INTER_CULTIVATION", label: "Inter-cultivation Equipment" },
  { id: "HARVESTING_THRESHING", label: "Harvesting & Threshing" },
  { id: "RESIDUE_MANAGEMENT", label: "Residue / Hay / Forage Equipment" },
  { id: "POST_HARVEST_VALUE", label: "Post-harvest & Value Addition" },
  { id: "PLANT_PROTECTION", label: "Plant Protection (Sprayers etc.)" },
  { id: "IRRIGATION_EQUIP", label: "Irrigation Equipments / Pumps" },
  { id: "SPECIALIZED_AGRI", label: "Specialized Agricultural Machinery" },
  { id: "MANUAL_ANIMAL", label: "Manual / Animal-drawn Implements" },
  { id: "OTHERS", label: "Other Farm Machinery" },
];

const getMachGroupLabel = (id) =>
  MACH_GROUPS.find((x) => x.id === id)?.label || id || "";

/* -------------------------
   TRACTOR (PTO categories)
------------------------- */
const TRACTOR_SUBSIDY_ITEMS = [
  { id: "TRACTOR_2WD_UPTO_20PTO", label: "Tractor 2WD (up to 20 PTO HP)" },
  { id: "TRACTOR_4WD_UPTO_20PTO", label: "Tractor 4WD (up to 20 PTO HP)" },
  { id: "TRACTOR_2WD_20_40PTO", label: "Tractor 2WD (above 20 PTO HP and up to 40 PTO HP)" },
  { id: "TRACTOR_4WD_20_40PTO", label: "Tractor 4WD (above 20 PTO HP and up to 40 PTO HP)" },
  { id: "TRACTOR_2WD_40_50PTO", label: "Tractor 2WD (above 40 PTO HP and up to 50 PTO HP)" },
  { id: "TRACTOR_4WD_40_50PTO", label: "Tractor 4WD (above 40 PTO HP and up to 50 PTO HP)" },
  { id: "TRACTOR_2WD_ABOVE_50PTO", label: "Tractor 2WD (Above 50 PTO HP)" },
  { id: "TRACTOR_4WD_ABOVE_50PTO", label: "Tractor 4WD (Above 50 PTO HP)" },
];
const getTractorSubsidyLabel = (id) =>
  TRACTOR_SUBSIDY_ITEMS.find((x) => x.id === id)?.label || id || "";

/* -------------------------
   MACHINERY ITEMS (Group → Items)
------------------------- */
const MACHINERY_MASTER = {
  TRACTORS: {
    label: "Tractors",
    items: TRACTOR_SUBSIDY_ITEMS.map((x) => ({ ...x })),
  },
  POWER_TILLERS: {
    label: "Power Tillers",
    items: ["Power Tiller (8 BHP and up to 11 BHP)", "Power Tiller (Above 11 BHP)"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  COMBINE_HARVESTERS: {
    label: "Combine Harvesters",
    items: [
      "Combine Harvester (Self-propelled)",
      "Combine Harvester - Tractor powered (without tractor)",
      "Combine Harvester - Tractor powered (with tractor)",
      "Combine Harvester - Chopper",
      "Combine Harvester (Track Type) - < 6 feet cutter bar width",
      "Combine Harvester (Track Type) - > 6 feet cutter bar width",
      "Combine Harvester for potato/groundnut/other tuber",
      "Sugarcane Harvester (Self-propelled) - Wheel/Track type",
      "Maize Combine Harvester (Self-propelled)",
    ].map((label) => ({ id: toId(label), label })),
  },
  KISAN_DRONE: {
    label: "Kisan Drone / Spraying Drone",
    items: [
      "Kisan Drone (Agriculture Spraying Drone)",
      "Drone Battery & Charger Set",
      "Drone Spray Pump / Nozzle Kit",
      "Drone Training / Demonstration Setup",
    ].map((label) => ({ id: toId(label), label })),
  },
  RICE_TRANSPLANTERS: {
    label: "Rice / Paddy Transplanters",
    items: [
      "Self-Propelled Rice Transplanter",
      "Self-Propelled Rice Transplanter - 4 rows",
      "Self-Propelled Rice Transplanter - above 8 rows",
    ].map((label) => ({ id: toId(label), label })),
  },
  SELF_PROPELLED_AGRI: {
    label: "Self-propelled Agri / Hort Machinery",
    items: [
      "Crop Reaper cum Binder",
      "Power Weeder (Engine Operated)",
      "Post Hole Digger / Auger (Self-propelled)",
      "Pneumatic / Other Planter",
      "Arecanut Climber Harvester",
      "Tea Leaf Harvester (Hand Held)",
      "Self-Propelled Tool Bar (Ride-on)",
      "Self-Propelled Electric Agricultural Tool Bar",
      "Track Trolley",
      "Multi Utility Machine with Drone Carrier",
      "Nursery Media Filling Machine",
      "Multipurpose Hydraulic",
      "Brush Cutter (Electric / Engine Powered)",
      "Self-propelled Maize Harvester",
      "Electric Tiller / Weeder",
      "Self-Propelled Forage Harvester (Single/Two/Four Row)",
    ].map((label) => ({ id: toId(label), label })),
  },
  LAND_DEV_TILLAGE: {
    label: "Land Development & Tillage Implements",
    items: [
      "MB Plough (Single Bottom)",
      "MB Plough (Single Bottom - Reversible)",
      "MB Plough (Two Bottom - Hydraulic Reversible)",
      "MB Plough (Two Bottom - Mechanical Reversible)",
      "Disc Plough",
      "Cultivator",
      "Harrow",
      "Leveler Blade",
      "Cage Wheel",
      "Furrow Opener",
      "Ridger",
      "Weed Slasher",
      "Bund Former",
      "Crust Breaker",
      "Rotopuddler",
      "Rotocultivator",
      "Power Harrow",
      "Rotavator (Up to 4 ft.)",
      "Rotavator (Up to 5 ft.)",
      "Chisel Plough",
      "Laser Land Leveller",
    ].map((label) => ({ id: toId(label), label })),
  },
  SOWING_PLANTING: {
    label: "Sowing / Planting / Seeding",
    items: [
      "Post Hole Digger",
      "Potato Planter",
      "Potato Digger",
      "Groundnut Digger",
      "Strip Till Drill",
      "Tractor Drawn Crop Reaper",
      "Onion Harvester",
      "Rice Straw Chopper",
      "Raised Bed Planter",
      "Sugarcane Cutter / Stripper",
      "Multi Crop Planter",
      "Ridge Furrow Planter",
      "Pneumatic Planter",
      "Pneumatic Vegetable Transplanter",
      "Pneumatic Vegetable Seeder",
      "Pneumatic Vegetable Seeder",
      "Plastic Mulch Laying Machine",
      "Seed Treating Drum",
      "Seed Cum Fertilizer Drill",
      "Aqua Ferti Seed Drill",
      "Lateral Laying Machine",
      "Direct Rice Seeder (DSR)",
      "Zero-Till Drill",
    ].map((label) => ({ id: toId(label), label })),
  },
  INTER_CULTIVATION: {
    label: "Inter-cultivation Equipment",
    items: [
      "Grass Weed Slasher",
      "Inter Row cum Intra Row Weeder",
      "Power Weeder (Engine Operated)",
      "Power Weeder (Electric)",
    ].map((label) => ({ id: toId(label), label })),
  },
  HARVESTING_THRESHING: {
    label: "Harvesting & Threshing",
    items: [
      "Ground Nut Pod Stripper",
      "Thresher",
      "Multi Crop Thresher",
      "Paddy Thresher",
      "Winnowing Fan",
      "Maize Sheller",
      "Mower",
      "Flail Harvester",
      "Mower Shredder (All Purpose / All Crops)",
      "Chaff Cutter (Operated by engine/electric motor below 3 HP)",
    ].map((label) => ({ id: toId(label), label })),
  },
  RESIDUE_MANAGEMENT: {
    label: "Residue / Hay / Forage Equipment",
    items: [
      "Sugarcane Trash Cutter",
      "Coconut Frond Chopper",
      "Stubble Shaver",
      "Straw Reaper",
      "Baler (Small Capacity)",
      "Baler (Round - up to 16 kg)",
      "Baler (Round - above 16 and up to 25 kg per bale)",
      "Feed Block Making Machine",
      "Straw Chopper / Shredder / Mulcher (Mounted type)",
    ].map((label) => ({ id: toId(label), label })),
  },
  POST_HARVEST_VALUE: {
    label: "Post-harvest & Value Addition",
    items: [
      "Grain Cleaner",
      "Grain Grader",
      "Seed Cleaner & Grader",
      "Mini Dal Mill",
      "Mini Rice Mill",
      "Flour Mill",
      "Oil Expeller",
      "Vegetable/Fruit Grader",
      "Packaging / Sealing Machine",
    ].map((label) => ({ id: toId(label), label })),
  },
  PLANT_PROTECTION: {
    label: "Plant Protection (Sprayers etc.)",
    items: ["Manual Knapsack Sprayer", "Battery Operated Sprayer", "Engine Operated Sprayer", "Boom Sprayer", "Orchard Sprayer", "Drone Sprayer"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  IRRIGATION_EQUIP: {
    label: "Irrigation Equipments / Pumps",
    items: ["Centrifugal Pump", "Submersible Pump", "Solar Pump", "Diesel Pump", "Electric Pump", "Pipe Laying / Carrying System"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  SPECIALIZED_AGRI: {
    label: "Specialized Agricultural Machinery",
    items: ["Horticulture Pruning Machine", "Orchard Platform / Lift", "Cotton Picker", "Sugarcane Bud Chipper", "Banana Fibre Extractor"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  MANUAL_ANIMAL: {
    label: "Manual / Animal-drawn Implements",
    items: ["Country Plough", "Blade Harrow", "Spike Tooth Harrow", "Animal Drawn Cultivator", "Manual Weeder", "Wheel Hoe", "Hand Hoe"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  OTHERS: {
    label: "Other Farm Machinery",
    items: ["Trailer / Trolley (Up to 3 Ton)", "Trailer / Trolley (Up to 5 Ton)", "Narrow Rear Rim (One Pair)", "Front Mounted Straw Reaper", "Tool Bar", "Track Trolley"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
};

/* =========================
   MONEY HELPERS
========================= */
const calcNum = (v) => Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

const INR = (n) => {
  const v = calcNum(n);
  return `₹${v.toLocaleString("en-IN")}`;
};

// PDF-safe money formatter (₹ not reliable in jsPDF default fonts)
const INR_PDF = (n) => {
  const v = calcNum(n);
  return `Rs. ${v.toLocaleString("en-IN")}`;
};

const inferUnitType = (unitText = "") => {
  const u = String(unitText || "").toLowerCase();
  if (u.includes("acre")) return "per acre";
  if (u.includes("hp")) return "per HP";
  if (u.includes("project")) return "per project";
  return "per unit";
};

/* =========================
   IMAGE HELPERS
========================= */

// old helper (kept; NOT removed)
async function urlToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

// NEW: auto-detect PNG/JPEG for jsPDF addImage
async function urlToDataUrlWithType(url) {
  if (!url) throw new Error("Letterhead URL missing.");

  // cache-bust safe (CDN issues avoid)
  const u = url.includes("?") ? `${url}&_ts=${Date.now()}` : `${url}?_ts=${Date.now()}`;

  let res;
  try {
    res = await fetch(u, { mode: "cors" });
  } catch (e) {
    throw new Error("Letterhead fetch failed (CORS). Please ensure Storage CORS is set for shineagri.org");
  }

  if (!res.ok) throw new Error(`Letterhead fetch failed: HTTP ${res.status}`);

  const blob = await res.blob();
  const mime = blob.type || "image/png";

  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });

  const type = mime.includes("jpeg") || mime.includes("jpg") ? "JPEG" : "PNG";
  return { dataUrl, type };
}

/* =========================
   QUOTE ITEMS BUILDER (FIXED)
========================= */
const buildQuoteItems = ({ selectedProduct, quoteApp, quoteForm }) => {
  const rawItems = Array.isArray(quoteForm?.items) ? quoteForm.items : [];

  // ✅ ONLY accept custom items if at least 1 row has particulars
  const customItems = rawItems
    .map((it, idx) => ({
      sr: idx + 1,
      particulars: String(it?.particulars || "").trim(),
      amount: calcNum(it?.amount),
    }))
    .filter((x) => x.particulars);

  if (customItems.length) return customItems;

  // ✅ fallback to default breakup
  const ex = calcNum(quoteForm?.offeredExShowroom || selectedProduct?.priceExShowroom);
  const rto = calcNum(quoteForm?.rtoTax || selectedProduct?.rtoTax);
  const ins = calcNum(quoteForm?.insuranceAmount || selectedProduct?.insuranceAmount);
  const acc = calcNum(quoteForm?.accessoriesAmount || selectedProduct?.accessoriesAmount);
  const oth = calcNum(quoteForm?.otherCharges || selectedProduct?.otherCharges);

  const mainPart =
    selectedProduct?.title ||
    `${quoteApp?.companyBrand || quoteApp?.brand || ""} ${quoteApp?.modelName || ""} ${
      quoteApp?.hpRating ? `(${quoteApp.hpRating})` : ""
    }`.trim() ||
    "Tractor / Machine";

  const rows = [
    { sr: 1, particulars: mainPart, amount: ex },
    { sr: 2, particulars: "RTO TAX", amount: rto },
    { sr: 3, particulars: "INSURANCE", amount: ins },
    { sr: 4, particulars: "ACCESSORIES", amount: acc },
  ];
  if (oth > 0) rows.push({ sr: rows.length + 1, particulars: "OTHER CHARGES", amount: oth });

  return rows;
};

/* =========================
   UI TOKENS (OUTSIDE) ✅
========================= */
const UI = {
  pageBg: "linear-gradient(180deg, #f6f7fb 0%, #eef2ff 45%, #f8fafc 100%)",
  surface: "rgba(255,255,255,0.92)",
  card: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(15,23,42,0.08)",
  shadow: "0 18px 45px rgba(15,23,42,0.10)",
  shadowSoft: "0 10px 30px rgba(15,23,42,0.08)",
  text: "#0f172a",
  muted: "rgba(15,23,42,0.65)",
  brand: "#6d28d9",
  brand2: "#4f46e5",
  ok: "#059669",
  warn: "#d97706",
  danger: "#e11d48",
};

const premiumPaperSx = {
  bgcolor: UI.card,
  border: UI.border,
  borderRadius: 3,
  boxShadow: UI.shadowSoft,
  backdropFilter: "blur(10px)",
};

/* =========================
   STABLE UI COMPONENTS ✅
   (Fixes focus/cursor jump 100%)
========================= */
function Section({ title, sub, children }) {
  return (
    <Paper sx={{ p: 2.6, ...premiumPaperSx }}>
      {title ? (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography sx={{ fontWeight: 950, color: UI.text, fontSize: 14 }}>
                {title}
              </Typography>
              {sub ? (
                <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.3 }}>
                  {sub}
                </Typography>
              ) : null}
            </Box>
            <Box
              sx={{
                width: 56,
                height: 18,
                borderRadius: 2,
                opacity: 0.35,
                backgroundImage: "radial-gradient(rgba(15,23,42,0.22) 1px, transparent 1px)",
                backgroundSize: "6px 6px",
              }}
            />
          </Stack>
        </Box>
      ) : null}
      {children}
    </Paper>
  );
}

function StatCard({ title, value, sub, tone = "brand" }) {
  const toneMap = {
    brand: { chipBg: "rgba(109,40,217,0.10)", chip: UI.brand },
    ok: { chipBg: "rgba(5,150,105,0.10)", chip: UI.ok },
    warn: { chipBg: "rgba(217,119,6,0.10)", chip: UI.warn },
    danger: { chipBg: "rgba(225,29,72,0.10)", chip: UI.danger },
    info: { chipBg: "rgba(79,70,229,0.10)", chip: UI.brand2 },
  };
  const t = toneMap[tone] || toneMap.brand;

  return (
    <Paper sx={{ p: 2.3, ...premiumPaperSx, position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          inset: -40,
          background: `radial-gradient(closest-side, ${t.chipBg}, rgba(255,255,255,0) 70%)`,
          pointerEvents: "none",
        }}
      />
      <Typography sx={{ fontSize: 11, color: UI.muted, fontWeight: 900, letterSpacing: 0.4 }}>
        {title}
      </Typography>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mt: 0.8 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 950, color: UI.text, lineHeight: 1 }}>
          {value}
        </Typography>
        <Box
          sx={{
            px: 1.2,
            py: 0.45,
            borderRadius: 999,
            bgcolor: t.chipBg,
            color: t.chip,
            border: "1px solid rgba(15,23,42,0.06)",
            fontSize: 11,
            fontWeight: 900,
          }}
        >
          LIVE
        </Box>
      </Stack>
      {sub ? <Typography sx={{ mt: 0.8, fontSize: 12, color: UI.muted }}>{sub}</Typography> : null}
    </Paper>
  );
}

function FormBlock({ code, title, sub, children }) {
  return (
    <Paper
      sx={{
        ...premiumPaperSx,
        p: 2.2,
        borderRadius: 3,
        border: "1px solid rgba(15,23,42,0.10)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          bgcolor: code === "A" ? "rgba(79,70,229,0.85)" : code === "B" ? "rgba(5,150,105,0.85)" : "rgba(109,40,217,0.85)",
        }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.4 }}>
        <Box
          sx={{
            width: 30,
            height: 26,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            fontWeight: 950,
            color: "#fff",
            bgcolor: code === "A" ? "rgba(79,70,229,0.92)" : code === "B" ? "rgba(5,150,105,0.92)" : "rgba(109,40,217,0.92)",
          }}
        >
          {code}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 950, color: UI.text }}>{title}</Typography>
          {sub ? <Typography sx={{ fontSize: 12, color: UI.muted }}>{sub}</Typography> : null}
        </Box>
      </Stack>
      {children}
    </Paper>
  );
}

/* =========================
   LABEL HELPER (subsidy label)
========================= */
const getSubsidyLabel = (machGroup, id) => {
  if (!id) return "";
  const g = machGroup || "TRACTORS";
  if (g === "TRACTORS") return getTractorSubsidyLabel(id);
  const items = MACHINERY_MASTER[g]?.items || [];
  const match = items.find((x) => String(x.id) === String(id));
  if (match?.label) return match.label;
  // fallback try match by label-toId
  const match2 = items.find((x) => String(toId(x.label)) === String(id));
  return match2?.label || id;
};

/* =========================
   MAIN
========================= */

export default function FarmerVendorDashboard() {
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  // products
  const [products, setProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  // ✅ product form errors (clean validation UI)
  const [productErrors, setProductErrors] = useState({});

  // product form
  const [productForm, setProductForm] = useState({
    name: "",
    category: "",
    unit: "",
    price: "",
    description: "",

    machGroup: "TRACTORS",
    companyBrand: "",
    branchLabel: "",

    machineType: "",
    brand: "",
    modelName: "",
    hpRating: "",
    driveType: "",
    fuelType: "",
    usageType: "",

    priceExShowroom: "",
    rtoTax: "",
    insuranceAmount: "",
    accessoriesAmount: "",
    otherCharges: "",
    onRoadPriceApprox: "",
    subsidyItemId: "",
    emiOptions: "",
    subsidySchemeTag: "",
    warranty: "",
    serviceSupport: "",
    rentRatePerHour: "",
    rentRatePerAcre: "",
    minBookingHours: "",
  });

  // filters
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [productSearch, setProductSearch] = useState("");

  // applications
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appStatusFilter, setAppStatusFilter] = useState("all");
  const [appSearch, setAppSearch] = useState("");

  // tab
  const [activeTab, setActiveTab] = useState("dashboard");

  // letterhead
  const [letterheadUploading, setLetterheadUploading] = useState(false);
  const [letterheadPreview, setLetterheadPreview] = useState("");

  // quotation modal
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteApp, setQuoteApp] = useState(null);
  const [quoteSaving, setQuoteSaving] = useState(false);

  // ✅ quoteForm extended (NO deletion)
  const [quoteForm, setQuoteForm] = useState({
    quotationNo: "",
    quotationDate: "",
    validityDays: "7",
    deliveryTime: "As per stock availability",
    paymentTerms: "As per mutual discussion",
    notes: "",
    selectedProductId: "",
    offeredOnRoad: "",
    offeredExShowroom: "",
    discount: "",
    contactPerson: "",
    contactMobile: "",

    // ✅ NEW: breakup + GST + multi-items
    gstRate: "",
    rtoTax: "",
    insuranceAmount: "",
    accessoriesAmount: "",
    otherCharges: "",
    items: [],
  });

  /* =========================
     ✅ Cursor / Focus FIX 100%
     - stable handler (useCallback)
     - always store string values
     - no inside-component UI components
  ========================= */
  const setPF = useCallback((name, value) => {
    const v = value === null || value === undefined ? "" : String(value);

    setProductErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    setProductForm((prev) => {
      if (name === "companyBrand") return { ...prev, companyBrand: v, brand: v };

      if (name === "category") {
        const nextCat = v;
        const isMach = toComponentId(nextCat) === "MACH_BANK";
        return {
          ...prev,
          category: nextCat,
          companyBrand: isMach ? prev.companyBrand || "" : "",
          brand: isMach ? prev.brand || prev.companyBrand || "" : "",
          machGroup: isMach ? prev.machGroup || "TRACTORS" : "TRACTORS",
          subsidyItemId: isMach ? prev.subsidyItemId : "",
        };
      }

      if (name === "machGroup") {
        return { ...prev, machGroup: v, subsidyItemId: "" };
      }

      return { ...prev, [name]: v };
    });
  }, []);

  /* =========================
     DERIVED / MEMO
  ========================= */

  const vendorApproved = vendor?.status === "approved";

  const availableCategories = useMemo(() => {
    const cats = vendor?.componentsOffered || vendor?.categories || [];
    return Array.from(new Set((cats || []).map(toComponentId)));
  }, [vendor]);

  const brandOptions = useMemo(() => {
    const picked = (vendor?.tractorBrands || []).filter(Boolean);
    const base = picked.length ? picked : TRACTOR_BRANDS_MASTER;
    return Array.from(new Set(base)).sort((a, b) => a.localeCompare(b));
  }, [vendor]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const id = p.componentId || p.category;
      if (id) set.add(id);
    });
    return Array.from(set);
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (productStatusFilter !== "all") {
      list = list.filter((p) => (p.status || "pending") === productStatusFilter);
    }
    if (productCategoryFilter !== "all") {
      list = list.filter((p) => (p.componentId || p.category) === productCategoryFilter);
    }
    const needle = productSearch.trim().toLowerCase();
    if (needle) {
      list = list.filter((p) => {
        const s = `${p.title || p.name || ""} ${p.companyBrand || ""} ${p.brand || ""} ${
          p.modelName || ""
        } ${p.machineType || ""} ${p.machGroup || ""}`.toLowerCase();
        return s.includes(needle);
      });
    }
    list.sort((a, b) => (a.title || p.name || "").localeCompare(b.title || b.name || ""));
    return list;
  }, [products, productStatusFilter, productCategoryFilter, productSearch]);

  const filteredApps = useMemo(() => {
    let list = [...applications];
    if (appStatusFilter !== "all") {
      list = list.filter((a) => (a.status || "submitted") === appStatusFilter);
    }
    const needle = appSearch.trim().toLowerCase();
    if (needle) {
      list = list.filter((a) => {
        const s = `${a.farmerName || ""} ${a.memberId || ""} ${a.machineType || ""} ${
          a.companyBrand || ""
        } ${a.brand || ""} ${a.modelName || ""} ${a.hpRating || ""} ${a.subsidyItemId || ""}`.toLowerCase();
        return s.includes(needle);
      });
    }
    return list;
  }, [applications, appStatusFilter, appSearch]);

  /* =========================
     AUTH + DATA LOAD
  ========================= */

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        window.location.href = "/vendor/login";
        return;
      }
      setUser(u);

      try {
        setAppsLoading(true);

        const refV = doc(db, "farmer_vendors", u.uid);
        const snap = await getDoc(refV);

        if (!snap.exists()) {
          alert("Vendor profile not found. Please contact Society for help.");
          setLoading(false);
          setAppsLoading(false);
          return;
        }

        const v = snap.data() || {};
        const rawCats = v.componentsOffered || v.categories || [];
        const canonicalCategories = Array.from(new Set((rawCats || []).map(toComponentId)));

        const vendorObj = {
          id: snap.id,
          ...v,
          componentsOffered: canonicalCategories,
          categories: canonicalCategories,
          letterhead_url: v.letterhead_url || "",
          tractorBrands: Array.isArray(v.tractorBrands) ? v.tractorBrands : [],

          // ✅ normalize vendor finance fields
          gstin: v.gstin || v.GSTIN || "",
          bank_name: v.bank_name || v.bankName || "",
          bank_ac: v.bank_ac || v.bankAc || v.bankAccount || "",
          bank_ifsc: v.bank_ifsc || v.bankIfsc || "",
          bank_branch: v.bank_branch || v.bankBranch || "",
          gst_rate: v.gst_rate || v.gstRate || "",
          email: v.email || v.vendorEmail || "",
        };

        setVendor(vendorObj);
        setLetterheadPreview(vendorObj.letterhead_url || "");

        // load products
        const pSnap = await getDocs(collection(db, "farmer_vendors", u.uid, "products"));
        const pList = [];
        pSnap.forEach((d) => {
          const data = d.data() || {};
          const compId = data.componentId || toComponentId(data.category);
          pList.push({
            id: d.id,
            ...data,
            componentId: compId,
            category: compId,
          });
        });
        setProducts(pList);

        // load applications
        const aSnap = await getDocs(
          query(collection(db, "machinery_applications"), where("vendorId", "==", u.uid))
        );
        const aList = [];
        aSnap.forEach((d) => aList.push({ id: d.id, ...d.data() }));
        aList.sort((a, b) => {
          const aa = a.createdAt?.toMillis?.() || 0;
          const bb = b.createdAt?.toMillis?.() || 0;
          return bb - aa;
        });
        setApplications(aList);
      } catch (err) {
        console.error("Vendor fetch error:", err);
        alert("Error loading vendor dashboard: " + err.message);
      } finally {
        setAppsLoading(false);
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/vendor/login";
  };

  /* =========================
     PRODUCT FORM HELPERS
  ========================= */

  const isMachinery = toComponentId(productForm.category) === "MACH_BANK";

  const exShowroom = calcNum(productForm.priceExShowroom);
  const rtoTax = calcNum(productForm.rtoTax);
  const insuranceAmount = calcNum(productForm.insuranceAmount);
  const accessoriesAmount = calcNum(productForm.accessoriesAmount);
  const otherCharges = calcNum(productForm.otherCharges);
  const computedOnRoad = exShowroom + rtoTax + insuranceAmount + accessoriesAmount + otherCharges;

  const subsidyOptions = useMemo(() => {
    const g = productForm.machGroup || "TRACTORS";
    const pack = MACHINERY_MASTER[g];
    const items = pack?.items || [];
    return items.map((it) => (typeof it === "string" ? { id: toId(it), label: it } : it));
  }, [productForm.machGroup]);

  const resetProductForm = (keepCategory = true) => {
    setEditingProductId(null);
    setProductErrors({});
    setProductForm((prev) => ({
      name: "",
      category: keepCategory ? prev.category : "",
      unit: "",
      price: "",
      description: "",

      machGroup: "TRACTORS",
      companyBrand: "",
      branchLabel: "",

      machineType: "",
      brand: "",
      modelName: "",
      hpRating: "",
      driveType: "",
      fuelType: "",
      usageType: "",

      priceExShowroom: "",
      rtoTax: "",
      insuranceAmount: "",
      accessoriesAmount: "",
      otherCharges: "",
      onRoadPriceApprox: "",
      subsidyItemId: "",
      emiOptions: "",
      subsidySchemeTag: "",
      warranty: "",
      serviceSupport: "",
      rentRatePerHour: "",
      rentRatePerAcre: "",
      minBookingHours: "",
    }));
  };

  const validateProduct = () => {
    const errs = {};
    if (!String(productForm.category || "").trim()) errs.category = "Please select Component.";
    if (!String(productForm.name || "").trim()) errs.name = "Please enter Product Title.";

    if (toComponentId(productForm.category) === "MACH_BANK") {
      if (!String(productForm.machGroup || "").trim()) errs.machGroup = "Please select Machinery Group.";
      if (!String(productForm.companyBrand || "").trim()) errs.companyBrand = "Please enter Company / Brand.";
      if (!String(productForm.subsidyItemId || "").trim()) errs.subsidyItemId = "Please select Govt Subsidy Item.";
    }
    return errs;
  };

  const addOrUpdateProduct = async (e) => {
    e.preventDefault();
    if (!user || !vendor) return;

    const errs = validateProduct();
    setProductErrors(errs);
    if (Object.keys(errs).length) return;

    const componentId = toComponentId(productForm.category);
    const isMach = componentId === "MACH_BANK";

    try {
      setSavingProduct(true);

      const title = productForm.name.trim();
      const description = productForm.description.trim();

      const basePrice = isMach ? exShowroom : calcNum(productForm.price);
      const unitType = isMach ? "per unit" : inferUnitType(productForm.unit);

      const payload = {
        title,
        componentId,
        description,
        unitType,
        basePrice,

        machGroup: isMach ? productForm.machGroup : "",
        companyBrand: isMach ? productForm.companyBrand : "",
        branchLabel: (productForm.branchLabel || "").trim(),

        brandName: isMach ? (productForm.companyBrand || productForm.brand || "").trim() : "",
        attachments: {},
        isActive: true,

        name: title,
        category: componentId,
        unit: (productForm.unit || "").trim(),
        price: calcNum(productForm.price),

        status: "pending",
        updated_at: serverTimestamp(),
      };

      if (isMach) {
        Object.assign(payload, {
          machineType: productForm.machineType.trim() || getMachGroupLabel(productForm.machGroup),
          brand: (productForm.companyBrand || productForm.brand || "").trim(),
          modelName: (productForm.modelName || "").trim(),
          hpRating: (productForm.hpRating || "").trim(),
          driveType: (productForm.driveType || "").trim(),
          fuelType: (productForm.fuelType || "").trim(),
          usageType: (productForm.usageType || "").trim(),

          priceExShowroom: exShowroom,
          rtoTax,
          insuranceAmount,
          accessoriesAmount,
          otherCharges,
          onRoadPriceApprox: computedOnRoad,

          subsidyItemId: productForm.subsidyItemId || "",
          emiOptions: (productForm.emiOptions || "").trim(),
          subsidySchemeTag: (productForm.subsidySchemeTag || "").trim(),
          warranty: (productForm.warranty || "").trim(),
          serviceSupport: (productForm.serviceSupport || "").trim(),
          rentRatePerHour: calcNum(productForm.rentRatePerHour),
          rentRatePerAcre: calcNum(productForm.rentRatePerAcre),
          minBookingHours: calcNum(productForm.minBookingHours),
        });
      }

      if (editingProductId) {
        const productDocRef = doc(db, "farmer_vendors", user.uid, "products", editingProductId);
        await updateDoc(productDocRef, payload);
        setProducts((prev) => prev.map((p) => (p.id === editingProductId ? { ...p, ...payload } : p)));
        alert("Product updated (Pending approval).");
      } else {
        const refP = collection(db, "farmer_vendors", user.uid, "products");
        payload.created_at = serverTimestamp();
        const docRef = await addDoc(refP, payload);
        setProducts((prev) => [...prev, { id: docRef.id, ...payload }]);
        alert("Product saved (Pending approval).");
      }

      resetProductForm(true);
      setActiveTab("products");
    } catch (err) {
      console.error("Save product error:", err);
      alert("Error saving product: " + err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const handleEditProduct = (p) => {
    setActiveTab("addProduct");
    setEditingProductId(p.id);
    setProductErrors({});

    const compId = p.componentId || p.category || "";

    setProductForm({
      name: String(p.title || p.name || ""),
      category: String(compId || ""),
      unit: String(p.unit || ""),
      price: p.price !== undefined && p.price !== null ? String(p.price) : (p.basePrice !== undefined && p.basePrice !== null ? String(p.basePrice) : ""),
      description: String(p.description || ""),

      machGroup: String(p.machGroup || "TRACTORS"),
      companyBrand: String(p.companyBrand || p.brand || ""),
      branchLabel: String(p.branchLabel || ""),

      machineType: String(p.machineType || ""),
      brand: String(p.brand || p.brandName || ""),
      modelName: String(p.modelName || ""),
      hpRating: String(p.hpRating || ""),
      driveType: String(p.driveType || ""),
      fuelType: String(p.fuelType || ""),
      usageType: String(p.usageType || ""),

      priceExShowroom: p.priceExShowroom !== undefined && p.priceExShowroom !== null ? String(p.priceExShowroom) : "",
      rtoTax: p.rtoTax !== undefined && p.rtoTax !== null ? String(p.rtoTax) : "",
      insuranceAmount: p.insuranceAmount !== undefined && p.insuranceAmount !== null ? String(p.insuranceAmount) : "",
      accessoriesAmount: p.accessoriesAmount !== undefined && p.accessoriesAmount !== null ? String(p.accessoriesAmount) : "",
      otherCharges: p.otherCharges !== undefined && p.otherCharges !== null ? String(p.otherCharges) : "",
      onRoadPriceApprox: p.onRoadPriceApprox !== undefined && p.onRoadPriceApprox !== null ? String(p.onRoadPriceApprox) : "",
      subsidyItemId: String(p.subsidyItemId || ""),
      emiOptions: String(p.emiOptions || ""),
      subsidySchemeTag: String(p.subsidySchemeTag || ""),
      warranty: String(p.warranty || ""),
      serviceSupport: String(p.serviceSupport || ""),
      rentRatePerHour: p.rentRatePerHour !== undefined && p.rentRatePerHour !== null ? String(p.rentRatePerHour) : "",
      rentRatePerAcre: p.rentRatePerAcre !== undefined && p.rentRatePerAcre !== null ? String(p.rentRatePerAcre) : "",
      minBookingHours: p.minBookingHours !== undefined && p.minBookingHours !== null ? String(p.minBookingHours) : "",
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteProduct = async (p) => {
    if (!user) return;
    const ok = window.confirm(`Delete product "${p.title || p.name}"?`);
    if (!ok) return;

    try {
      const productDocRef = doc(db, "farmer_vendors", user.uid, "products", p.id);
      await deleteDoc(productDocRef);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      if (editingProductId === p.id) resetProductForm(false);
      alert("Product deleted.");
    } catch (err) {
      console.error("Delete product error:", err);
      alert("Error deleting product: " + err.message);
    }
  };

  /* =========================
     LETTERHEAD UPLOAD
  ========================= */

  const handleLetterheadUpload = async (file) => {
    if (!user || !vendor) return;
    if (!file) return;

    const ok = window.confirm(
      "Upload this image as your official Letterhead?\n\n(Recommendation: A4 width image, white background, PNG preferred)"
    );
    if (!ok) return;

    try {
      setLetterheadUploading(true);

      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const storagePath = `vendor_letterheads/${user.uid}/letterhead.${ext}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "farmer_vendors", user.uid), {
        letterhead_url: url,
        letterhead_updated_at: serverTimestamp(),
      });

      setVendor((prev) => ({ ...prev, letterhead_url: url }));
      setLetterheadPreview(url);

      alert("Letterhead uploaded successfully.");
    } catch (err) {
      console.error("Letterhead upload error:", err);
      alert("Error uploading letterhead: " + err.message);
    } finally {
      setLetterheadUploading(false);
    }
  };

  /* =========================
     APPLICATIONS + QUOTATION
  ========================= */

  const handleMarkApplicationProcessed = async (a) => {
    if (!user) return;
    if ((a.status || "submitted") === "processed") return;

    if (!vendorApproved) {
      alert("Your profile is pending approval. You can view requests, but confirming is allowed only after approval.");
      return;
    }

    const ok = window.confirm(
      `Mark as CONFIRMED / PROCESSED?\n\n${a.farmerName || ""} – ${a.companyBrand || a.brand || ""} ${a.modelName || ""}`
    );
    if (!ok) return;

    try {
      const appRef = doc(db, "machinery_applications", a.id);
      await updateDoc(appRef, {
        status: "processed",
        processedAt: serverTimestamp(),
        processedByVendorId: user.uid,
      });

      setApplications((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: "processed" } : x)));

      alert("Application marked as processed.");
    } catch (err) {
      console.error("App status update error:", err);
      alert("Error updating application: " + err.message);
    }
  };

  const applySelectedProductToQuoteForm = (productId, { overwrite = true } = {}) => {
    const match = products.find((p) => p.id === productId) || null;
    const vendorDefaultGst = String(vendor?.gst_rate || vendor?.gstRate || "").trim();

    setQuoteForm((prev) => {
      if (!match) return { ...prev, selectedProductId: productId };

      const next = { ...prev, selectedProductId: productId };

      const ex = match?.priceExShowroom ?? "";
      const onRoad = match?.onRoadPriceApprox ?? "";

      if (overwrite || !String(prev.offeredExShowroom || "").trim())
        next.offeredExShowroom = ex !== "" ? String(ex) : "";
      if (overwrite || !String(prev.offeredOnRoad || "").trim())
        next.offeredOnRoad = onRoad !== "" ? String(onRoad) : "";

      if (overwrite || !String(prev.rtoTax || "").trim())
        next.rtoTax = match?.rtoTax !== undefined ? String(match.rtoTax) : "";
      if (overwrite || !String(prev.insuranceAmount || "").trim())
        next.insuranceAmount = match?.insuranceAmount !== undefined ? String(match.insuranceAmount) : "";
      if (overwrite || !String(prev.accessoriesAmount || "").trim())
        next.accessoriesAmount = match?.accessoriesAmount !== undefined ? String(match.accessoriesAmount) : "";
      if (overwrite || !String(prev.otherCharges || "").trim())
        next.otherCharges = match?.otherCharges !== undefined ? String(match.otherCharges) : "";

      if (!String(prev.gstRate || "").trim()) next.gstRate = vendorDefaultGst || prev.gstRate || "";

      return next;
    });
  };

  const openQuotation = (a) => {
    if (!vendorApproved) {
      alert("Your profile is pending approval. Quotation generation will be enabled after Society approval.");
      return;
    }

    const match = products.find((p) => {
      const compId = p.componentId || p.category;
      if (compId !== "MACH_BANK") return false;

      const sameCompany =
        (p.companyBrand || p.brand || "").toLowerCase() === (a.companyBrand || a.brand || "").toLowerCase();

      const sameModel = (p.modelName || "").toLowerCase() === (a.modelName || "").toLowerCase();

      const sameSubsidy = a.subsidyItemId ? (p.subsidyItemId || "") === a.subsidyItemId : true;

      return (sameCompany && sameModel && sameSubsidy) || (sameCompany && sameModel);
    });

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    const vendorDefaultGst = String(vendor?.gst_rate || vendor?.gstRate || "").trim();

    setQuoteApp(a);

    setQuoteForm((prev) => ({
      ...prev,
      quotationNo: prev.quotationNo || `QTN-${yyyy}-${String(a.id || "").slice(-6)}`,
      quotationDate: prev.quotationDate || `${yyyy}-${mm}-${dd}`,
      selectedProductId: match?.id || "",
      offeredExShowroom: match?.priceExShowroom ? String(match.priceExShowroom) : "",
      offeredOnRoad: match?.onRoadPriceApprox ? String(match.onRoadPriceApprox) : "",
      discount: "",
      notes: "",
      contactPerson: vendor?.contact_name || "",
      contactMobile: vendor?.mobile || "",

      rtoTax: match?.rtoTax ? String(match.rtoTax) : "",
      insuranceAmount: match?.insuranceAmount ? String(match.insuranceAmount) : "",
      accessoriesAmount: match?.accessoriesAmount ? String(match.accessoriesAmount) : "",
      otherCharges: match?.otherCharges ? String(match.otherCharges) : "",
      gstRate: prev.gstRate || vendorDefaultGst || "",

      items: Array.isArray(prev.items) ? prev.items : [],
    }));

    setQuoteOpen(true);
  };

  const closeQuotation = () => {
    setQuoteOpen(false);
    setQuoteApp(null);
  };

  const uploadQuotationPdfToStorage = async ({ pdfBlob, quotationNo }) => {
    if (!user) throw new Error("No user session.");
    const safeNo = String(quotationNo || "quotation").replace(/[^a-zA-Z0-9_\-]/g, "_");
    const storagePath = `vendor_quotations/${user.uid}/${safeNo}.pdf`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, pdfBlob, { contentType: "application/pdf" });
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const formatDDMMYYYY = (isoDate) => {
    if (!isoDate) return "";
    const s = String(isoDate);
    if (s.includes("-")) {
      const [y, m, d] = s.split("-");
      if (y && m && d) return `${d}-${m}-${y}`;
    }
    return s;
  };

  const generateQuotationPDF = async ({ saveToFirestore = true } = {}) => {
    if (!user || !vendor || !quoteApp) return;

    const selectedProduct = products.find((p) => p.id === quoteForm.selectedProductId) || null;

    if (!vendor.letterhead_url) {
      alert("Please upload your Letterhead first (Settings → Letterhead).");
      setActiveTab("settings");
      return;
    }
    if (!quoteForm.quotationNo || !quoteForm.quotationDate) {
      alert("Please fill Quotation No and Date.");
      return;
    }

    try {
      setQuoteSaving(true);

      const docPdf = new jsPDF("p", "mm", "a4");
      const pageW = docPdf.internal.pageSize.getWidth();
      const pageH = docPdf.internal.pageSize.getHeight();

      const M = 16;
      const R = pageW - M;

      const yTitle = 55;
      const yMeta = 70;
      const yTable = 92;

      const { dataUrl: lhDataUrl, type: lhType } = await urlToDataUrlWithType(vendor.letterhead_url);
      docPdf.addImage(lhDataUrl, lhType, 0, 0, pageW, pageH);

      docPdf.setFont("helvetica", "bold");
      docPdf.setFontSize(15);
      docPdf.text("QUOTATION", pageW / 2, yTitle, { align: "center" });

      docPdf.setFont("helvetica", "normal");
      docPdf.setFontSize(10.5);

      const farmerName = (quoteApp.farmerName || "-").toUpperCase();
      const village = quoteApp.farmerVillage || "-";
      const taluka = quoteApp.farmerTaluka || "-";
      const dist = quoteApp.farmerDistrict || "-";
      const addressLine = `${village}, Taluka: ${taluka}, Dist: ${dist}`;

      const refNo = quoteForm.quotationNo || "-";
      const dateDDMMYYYY = formatDDMMYYYY(quoteForm.quotationDate);

      docPdf.setFont("helvetica", "bold");
      docPdf.text("Farmer Name :", M, yMeta);
      docPdf.setFont("helvetica", "normal");
      docPdf.text(farmerName, M + 28, yMeta);

      docPdf.setFont("helvetica", "bold");
      docPdf.text("Address :", M, yMeta + 6);
      docPdf.setFont("helvetica", "normal");
      const addrLines = docPdf.splitTextToSize(addressLine, pageW - M * 2 - 70);
      docPdf.text(addrLines, M + 18, yMeta + 6);

      docPdf.setFont("helvetica", "bold");
      docPdf.text("Date :", R - 46, yMeta, { align: "left" });
      docPdf.setFont("helvetica", "normal");
      docPdf.text(dateDDMMYYYY, R, yMeta, { align: "right" });

      docPdf.setFont("helvetica", "bold");
      docPdf.text("Ref :", R - 46, yMeta + 6, { align: "left" });
      docPdf.setFont("helvetica", "normal");
      docPdf.text(refNo, R, yMeta + 6, { align: "right" });

      const items = buildQuoteItems({ selectedProduct, quoteApp, quoteForm }) || [];
      const subTotal = items.reduce((s, r) => s + calcNum(r.amount), 0);

      const gstPct = calcNum(quoteForm.gstRate || vendor?.gst_rate || vendor?.gstRate || "");
      const gstAmt = gstPct > 0 ? (subTotal * gstPct) / 100 : 0;
      const grandTotal = subTotal + gstAmt;

      const tableRows = items.map((it) => [
        String(it.sr || ""),
        String(it.particulars || ""),
        `${Math.round(calcNum(it.amount)).toLocaleString("en-IN")}/-`,
      ]);

      tableRows.push(["", "TOTAL", `${Math.round(subTotal).toLocaleString("en-IN")}/-`]);

      if (gstPct > 0) {
        tableRows.push(["", `GST (${gstPct}%)`, `${Math.round(gstAmt).toLocaleString("en-IN")}/-`]);
        tableRows.push(["", "GRAND TOTAL", `${Math.round(grandTotal).toLocaleString("en-IN")}/-`]);
      }

      autoTable(docPdf, {
        startY: yTable,
        theme: "grid",
        margin: { left: M, right: M },
        head: [["Sr No.", "Particulars", "Amount Rupees"]],
        body: tableRows,

        styles: {
          font: "helvetica",
          fontSize: 10.8,
          cellPadding: 3,
          lineWidth: 0.2,
          lineColor: [0, 0, 0],
          textColor: [0, 0, 0],
          valign: "middle",
        },

        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          lineWidth: 0.2,
          halign: "center",
        },

        columnStyles: {
          0: { cellWidth: 16, halign: "center" },
          1: { cellWidth: pageW - M * 2 - 16 - 45, halign: "left" },
          2: { cellWidth: 45, halign: "right" },
        },

        didParseCell: (data) => {
          const label = String(data.row?.raw?.[1] || "").toUpperCase();
          if (label === "TOTAL" || label.startsWith("GST") || label === "GRAND TOTAL") {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.lineWidth = 0.35;
          }
        },
      });

      let yAfter = (docPdf.lastAutoTable?.finalY || yTable) + 8;

      docPdf.setFont("helvetica", "normal");
      docPdf.setFontSize(9.8);
      const noteText =
        quoteForm.notes?.trim() ||
        "Note: Prices as applicable at the time of delivery. No interest will be paid on booking/deposit amount. Accessories/charges as applicable.";
      const noteLines = docPdf.splitTextToSize(noteText, pageW - M * 2);
      docPdf.text(noteLines, M, yAfter);
      yAfter += noteLines.length * 5 + 10;

      docPdf.setFontSize(11);
      const footY = Math.min(yAfter + 10, pageH - 28);

      docPdf.text("Customer's Signature", M, footY);
      docPdf.text(`For, ${vendor.company_name || "Dev Tractors"}`, R, footY, { align: "right" });

      const bankName = vendor.bank_name || "";
      const bankAc = vendor.bank_ac || "";
      const bankIfsc = vendor.bank_ifsc || "";
      const bankBranch = vendor.bank_branch || "";
      const gstin = vendor.gstin || "";

      let yBank = Math.min(footY + 9, pageH - 12);
      docPdf.setFontSize(9.5);

      if (gstin) {
        docPdf.text(`GSTIN: ${gstin}`, M, yBank);
        yBank += 5;
      }

      const hasBank = bankName || bankAc || bankIfsc || bankBranch;
      if (hasBank) {
        const bankLine = `BANK: ${bankName}   A/C: ${bankAc}   IFSC: ${bankIfsc}   BRANCH: ${bankBranch}`.trim();
        const bankLines = docPdf.splitTextToSize(bankLine, pageW - M * 2);
        docPdf.text(bankLines, M, yBank);
      }

      const pdfBlob = docPdf.output("blob");
      docPdf.save(`${quoteForm.quotationNo || "quotation"}.pdf`);

      if (saveToFirestore) {
        let pdfUrl = "";
        try {
          const safeName = `${(quoteForm.quotationNo || "quotation").replace(/[^\w\-]+/g, "_")}.pdf`;
          const storagePath = `vendor_quotations/${user.uid}/${Date.now()}_${safeName}`;
          const pdfRef = ref(storage, storagePath);
          await uploadBytes(pdfRef, pdfBlob, { contentType: "application/pdf" });
          pdfUrl = await getDownloadURL(pdfRef);
        } catch (e) {
          alert("PDF downloaded, but Storage upload failed: " + (e?.message || e));
          return;
        }

        try {
          const vendorEmail = vendor.email || vendor.vendorEmail || user.email || "";

          const offerEx =
            quoteForm.offeredExShowroom ||
            (selectedProduct?.priceExShowroom ? String(selectedProduct.priceExShowroom) : "");
          const offerOnRoad =
            quoteForm.offeredOnRoad ||
            (selectedProduct?.onRoadPriceApprox ? String(selectedProduct.onRoadPriceApprox) : "");

          const refQ = collection(db, "vendor_quotations");
          const qDoc = await addDoc(refQ, {
            vendorId: user.uid,
            vendorName: vendor.company_name || "",
            vendorEmail,
            applicationId: quoteApp.id,

            quotationNo: quoteForm.quotationNo,
            quotationDate: quoteForm.quotationDate,

            selectedProductId: quoteForm.selectedProductId || "",
            offeredExShowroom: offerEx || "",
            offeredOnRoad: offerOnRoad || "",
            discount: quoteForm.discount || "",

            items,
            subTotal,
            gstRate: gstPct || "",
            gstAmount: gstAmt,
            grandTotal,

            gstin: gstin || "",
            bank_name: bankName || "",
            bank_ac: bankAc || "",
            bank_ifsc: bankIfsc || "",
            bank_branch: bankBranch || "",

            pdfUrl,

            machGroup: quoteApp.machGroup || "",
            companyBrand: quoteApp.companyBrand || quoteApp.brand || "",

            farmerName: quoteApp.farmerName || "",
            farmerMobile: quoteApp.farmerMobile || "",
            memberId: quoteApp.memberId || "",

            created_at: serverTimestamp(),
          });

          await updateDoc(doc(db, "machinery_applications", quoteApp.id), {
            quotationGenerated: true,
            quotationNo: quoteForm.quotationNo,
            quotationAt: serverTimestamp(),
            quotationPdfUrl: pdfUrl,
            quotationDocId: qDoc.id,
          });

          setApplications((prev) =>
            prev.map((x) =>
              x.id === quoteApp.id
                ? { ...x, quotationGenerated: true, quotationNo: quoteForm.quotationNo, quotationPdfUrl: pdfUrl }
                : x
            )
          );
        } catch (e) {
          alert("PDF downloaded & uploaded, but Firestore save/update failed (rules): " + (e?.message || e));
          return;
        }
      }

      alert(saveToFirestore ? "Quotation PDF saved + downloaded successfully." : "Quotation PDF downloaded successfully.");
      closeQuotation();
    } catch (err) {
      console.error("Quotation PDF error:", err);
      alert("Error generating quotation: " + (err?.message || err));
    } finally {
      setQuoteSaving(false);
    }
  };

  /* =========================
     CSV EXPORTS
  ========================= */

  const downloadApplicationsCsv = () => {
    if (!applications.length) return alert("No applications to export.");

    const header = [
      "Application ID",
      "Farmer Name",
      "Membership ID",
      "Mobile",
      "District",
      "Taluka",
      "Village",
      "Mach Group",
      "Company",
      "Model",
      "HP",
      "Drive",
      "Subsidy Item",
      "On-road",
      "Status",
      "Quotation Generated",
      "Quotation No",
    ];

    const rows = applications.map((a) => {
      const vals = [
        a.id || "",
        a.farmerName || "",
        a.memberId || "",
        a.farmerMobile || "",
        a.farmerDistrict || "",
        a.farmerTaluka || "",
        a.farmerVillage || "",
        a.machGroup ? getMachGroupLabel(a.machGroup) : "",
        a.companyBrand || a.brand || "",
        a.modelName || "",
        a.hpRating || "",
        a.driveType || "",
        a.subsidyItemId ? getSubsidyLabel(a.machGroup, a.subsidyItemId) : "",
        a.onRoadPriceApprox || "",
        a.status || "submitted",
        a.quotationGenerated ? "YES" : "NO",
        a.quotationNo || "",
      ];
      return vals.map((v) => `"${String(v).replace(/"/g, '""')}"`);
    });

    const csv = [header.map((h) => `"${h}"`).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const aTag = document.createElement("a");
    aTag.href = url;
    aTag.download = "machinery_applications_report.csv";
    aTag.click();
    URL.revokeObjectURL(url);
  };

  const downloadProductsCsv = () => {
    if (!products.length) return alert("No products to export.");

    const header = [
      "Product ID",
      "Title",
      "Component",
      "Mach Group",
      "Company",
      "Model",
      "HP",
      "Subsidy Item",
      "Ex-showroom",
      "On-road",
      "Status",
    ];

    const rows = products.map((p) => {
      const compId = p.componentId || toComponentId(p.category);
      const isMach = compId === "MACH_BANK";

      const vals = [
        p.id || "",
        p.title || p.name || "",
        COMPONENT_LABELS[compId] || compId || "",
        p.machGroup ? getMachGroupLabel(p.machGroup) : "",
        p.companyBrand || p.brand || "",
        p.modelName || "",
        p.hpRating || "",
        p.subsidyItemId ? getSubsidyLabel(p.machGroup, p.subsidyItemId) : "",
        isMach ? p.priceExShowroom || "" : p.basePrice || p.price || "",
        isMach ? p.onRoadPriceApprox || "" : "",
        p.status || "pending",
      ];
      return vals.map((v) => `"${String(v).replace(/"/g, '""')}"`);
    });

    const csv = [header.map((h) => `"${h}"`).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const aTag = document.createElement("a");
    aTag.href = url;
    aTag.download = "vendor_products_catalogue.csv";
    aTag.click();
    URL.revokeObjectURL(url);
  };

  /* =========================
     UI (MUI Admin Dashboard)
  ========================= */

  const MAIN_NAV_H = 195;
  const VENDOR_BAR_H = 64;

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", background: UI.pageBg }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography sx={{ color: UI.muted, fontWeight: 900, fontSize: 12 }}>
            Loading vendor dashboard…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!vendor) return null;

  const totalProducts = products.length;
  const approvedCount = products.filter((p) => p.status === "approved").length;
  const pendingCount = products.filter((p) => !p.status || p.status === "pending").length;
  const rejectedCount = products.filter((p) => p.status === "rejected").length;

  const drawerW = 290;

  const MENU = [
    { id: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
    { id: "addProduct", label: "Add / Edit Product", icon: <AddCircleOutlineIcon /> },
    { id: "products", label: "Product Catalogue", icon: <Inventory2Icon /> },
    { id: "applications", label: "Farmer Requests", icon: <AssignmentIcon /> },
    { id: "settings", label: "Settings (Letterhead)", icon: <SettingsIcon /> },
    { id: "reports", label: "Reports / CSV", icon: <DescriptionIcon /> },
  ];

  const tabTitle =
    activeTab === "dashboard"
      ? "Dashboard Overview"
      : activeTab === "settings"
      ? "Settings"
      : activeTab === "addProduct"
      ? "Add / Edit Product"
      : activeTab === "products"
      ? "Product Catalogue"
      : activeTab === "applications"
      ? "Farmer Requests"
      : activeTab === "reports"
      ? "Reports"
      : "Module";

  return (
    <Box sx={{ minHeight: "100vh", background: UI.pageBg }}>
      {/* Vendor top bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: `${MAIN_NAV_H}px`,
          zIndex: 1100,
          bgcolor: "rgba(15,23,42,0.92)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Toolbar sx={{ display: "flex", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 950, letterSpacing: 0.4 }}>
            Shine Agri • Vendor Control Room
          </Typography>

          <Chip
            size="small"
            label={vendorApproved ? "✅ Profile Approved" : "⏳ Profile Pending"}
            sx={{
              bgcolor: vendorApproved ? "rgba(16,185,129,0.14)" : "rgba(245,158,11,0.14)",
              color: "#e5e7eb",
              border: "1px solid rgba(255,255,255,0.12)",
              fontWeight: 900,
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Chip
            size="small"
            label={vendor?.letterhead_url ? "Letterhead: Uploaded" : "Letterhead: Missing"}
            sx={{
              bgcolor: vendor?.letterhead_url ? "rgba(16,185,129,0.14)" : "rgba(244,63,94,0.14)",
              color: "#e5e7eb",
              border: "1px solid rgba(255,255,255,0.12)",
              fontWeight: 900,
            }}
          />

          <Button
            onClick={handleLogout}
            variant="contained"
            startIcon={<LogoutIcon />}
            sx={{
              bgcolor: "rgba(255,255,255,0.09)",
              color: "#e5e7eb",
              fontWeight: 950,
              borderRadius: 999,
              "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex" }}>
        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerW,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              zIndex: 1050,
              width: drawerW,
              boxSizing: "border-box",
              bgcolor: "rgba(255,255,255,0.78)",
              backdropFilter: "blur(14px)",
              color: UI.text,
              borderRight: "1px solid rgba(15,23,42,0.08)",
              top: `${MAIN_NAV_H + VENDOR_BAR_H}px`,
              height: `calc(100vh - ${MAIN_NAV_H + VENDOR_BAR_H}px)`,
            },
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 950, fontSize: 15, color: UI.text }}>
              {vendor?.company_name || "Vendor"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: UI.muted }}>
              District: <b>{vendor?.district || "-"}</b> • Taluka: <b>{vendor?.taluka || "-"}</b>
            </Typography>

            <Grid container spacing={1} sx={{ mt: 1.5 }}>
              <Grid item xs={4}>
                <Paper sx={{ p: 1.1, ...premiumPaperSx, boxShadow: "none" }}>
                  <Typography sx={{ fontSize: 10, color: UI.muted, fontWeight: 900 }}>Products</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 950, color: UI.text }}>{totalProducts}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 1.1, ...premiumPaperSx, boxShadow: "none" }}>
                  <Typography sx={{ fontSize: 10, color: UI.muted, fontWeight: 900 }}>Requests</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 950, color: UI.text }}>{applications.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 1.1, ...premiumPaperSx, boxShadow: "none" }}>
                  <Typography sx={{ fontSize: 10, color: UI.muted, fontWeight: 900 }}>Approved</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 950, color: UI.text }}>{approvedCount}</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "rgba(15,23,42,0.08)" }} />

          <Box sx={{ p: 1.2 }}>
            {MENU.map((m) => {
              const active = activeTab === m.id;
              return (
                <Button
                  key={m.id}
                  fullWidth
                  onClick={() => setActiveTab(m.id)}
                  startIcon={m.icon}
                  sx={{
                    justifyContent: "flex-start",
                    mb: 1,
                    borderRadius: 3,
                    fontWeight: 950,
                    textTransform: "none",
                    py: 1.15,
                    color: active ? UI.brand : UI.text,
                    bgcolor: active ? "rgba(109,40,217,0.10)" : "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(15,23,42,0.08)",
                    "&:hover": { bgcolor: active ? "rgba(109,40,217,0.12)" : "rgba(255,255,255,0.75)" },
                  }}
                >
                  {m.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ mt: "auto", p: 2, fontSize: 11, color: UI.muted }}>
            Tip: Letterhead → Products → Requests → Quotation PDF
          </Box>
        </Drawer>

        {/* Main */}
        <Box sx={{ flex: 1, p: 3.2, pt: `${MAIN_NAV_H + VENDOR_BAR_H + 16}px` }}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2.4, ...premiumPaperSx }}>
              <Typography sx={{ fontSize: 11, color: UI.muted, fontWeight: 900, letterSpacing: 0.5 }}>
                Vendor Workspace
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 950, color: UI.text }}>
                {tabTitle}
              </Typography>
              <Typography sx={{ fontSize: 12, color: UI.muted }}>
                Company: <b>{vendor?.company_name || "-"}</b> • District: <b>{vendor?.district || "-"}</b> • Taluka:{" "}
                <b>{vendor?.taluka || "-"}</b>
              </Typography>
            </Paper>

            {/* DASHBOARD */}
            {activeTab === "dashboard" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <StatCard
                    title="Profile"
                    value={vendorApproved ? "Approved" : "Pending"}
                    sub={vendorApproved ? "Quotation enabled" : "Wait for approval"}
                    tone={vendorApproved ? "ok" : "warn"}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard
                    title="Products"
                    value={totalProducts}
                    sub={`Approved: ${approvedCount} • Pending: ${pendingCount}`}
                    tone="info"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard title="Requests" value={applications.length} sub="Farmer quotation requests" tone="brand" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StatCard
                    title="Letterhead"
                    value={vendor?.letterhead_url ? "Uploaded" : "Missing"}
                    sub="Required for PDF quotation"
                    tone={vendor?.letterhead_url ? "ok" : "danger"}
                  />
                </Grid>
              </Grid>
            )}

            {/* SETTINGS (Letterhead) */}
            {activeTab === "settings" && (
              <Section title="Settings → Vendor Letterhead" sub="Upload your letterhead (PNG/JPG). Quotation PDFs will print on it.">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, ...premiumPaperSx }}>
                      <Typography sx={{ fontWeight: 950, color: UI.text, fontSize: 12 }}>
                        Upload Letterhead
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <input
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={(e) => handleLetterheadUpload(e.target.files?.[0])}
                          disabled={letterheadUploading}
                        />
                      </Box>
                      <Typography sx={{ mt: 1, fontSize: 11, color: UI.muted }}>
                        Recommended: A4 scan, white background, PNG preferred.
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center" }}>
                        <Chip
                          size="small"
                          label={vendor?.letterhead_url ? "Uploaded" : "Not Uploaded"}
                          sx={{
                            bgcolor: vendor?.letterhead_url ? "rgba(5,150,105,0.10)" : "rgba(217,119,6,0.12)",
                            color: vendor?.letterhead_url ? UI.ok : UI.warn,
                            border: "1px solid rgba(15,23,42,0.08)",
                            fontWeight: 950,
                          }}
                        />
                        {letterheadUploading ? (
                          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <CircularProgress size={16} />
                            <Typography sx={{ fontSize: 12, color: UI.muted }}>Uploading…</Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, ...premiumPaperSx }}>
                      <Typography sx={{ fontWeight: 950, color: UI.text, fontSize: 12 }}>
                        Preview
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {letterheadPreview ? (
                          <img
                            src={letterheadPreview}
                            alt="Letterhead Preview"
                            style={{ width: "100%", borderRadius: 12, border: "1px solid rgba(15,23,42,0.08)" }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 12, color: UI.muted }}>
                            No letterhead uploaded yet.
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Section>
            )}

            {/* ADD PRODUCT (Professional Form A/B/C) */}
            {activeTab === "addProduct" && (
              <Section
                title={editingProductId ? "Edit Product" : "Add Product"}
                sub="Machinery products: Sub-category + Company + Subsidy item required. All saves go in pending status (admin approval)."
              >
                {/* brand datalist (suggestion only) */}
                <datalist id="brand-list">
                  {brandOptions.map((b) => (
                    <option key={b} value={b} />
                  ))}
                </datalist>

                <Box component="form" onSubmit={addOrUpdateProduct}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        {/* A */}
                        <FormBlock code="A" title="Basic Details" sub="Product title, component, description.">
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                select
                                fullWidth
                                required
                                size="small"
                                label="Component *"
                                value={productForm.category}
                                onChange={(e) => setPF("category", e.target.value)}
                                error={!!productErrors.category}
                                helperText={productErrors.category || " "}
                                sx={{ bgcolor: "white", borderRadius: 2 }}
                              >
                                <MenuItem value="">Select component</MenuItem>
                                {availableCategories.map((id) => (
                                  <MenuItem key={id} value={id}>
                                    {COMPONENT_LABELS[id] || id}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            <Grid item xs={12} md={8}>
                              <TextField
                                fullWidth
                                required
                                size="small"
                                label="Product Title *"
                                value={productForm.name}
                                onChange={(e) => setPF("name", e.target.value)}
                                error={!!productErrors.name}
                                helperText={productErrors.name || " "}
                                sx={{ bgcolor: "white", borderRadius: 2 }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label={isMachinery ? "Usage Type (optional)" : "Unit (optional)"}
                                value={isMachinery ? productForm.usageType : productForm.unit}
                                onChange={(e) => setPF(isMachinery ? "usageType" : "unit", e.target.value)}
                                sx={{ bgcolor: "white", borderRadius: 2 }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Branch / Location (optional)"
                                value={productForm.branchLabel}
                                onChange={(e) => setPF("branchLabel", e.target.value)}
                                sx={{ bgcolor: "white", borderRadius: 2 }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                size="small"
                                label="Description"
                                value={productForm.description}
                                onChange={(e) => setPF("description", e.target.value)}
                                sx={{ bgcolor: "white", borderRadius: 2 }}
                              />
                            </Grid>
                          </Grid>
                        </FormBlock>

                        {/* B */}
                        {isMachinery && (
                          <FormBlock
                            code="B"
                            title="Machine Details"
                            sub="Machinery group, company/brand, model and govt subsidy item."
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  select
                                  fullWidth
                                  required
                                  size="small"
                                  label="Machinery Group *"
                                  value={productForm.machGroup}
                                  onChange={(e) => setPF("machGroup", e.target.value)}
                                  error={!!productErrors.machGroup}
                                  helperText={productErrors.machGroup || " "}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                >
                                  {MACH_GROUPS.map((g) => (
                                    <MenuItem key={g.id} value={g.id}>
                                      {g.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  required
                                  size="small"
                                  label="Company / Brand *"
                                  value={productForm.companyBrand}
                                  onChange={(e) => setPF("companyBrand", e.target.value)}
                                  inputProps={{ list: "brand-list" }}
                                  error={!!productErrors.companyBrand}
                                  helperText={productErrors.companyBrand || " "}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Model Name"
                                  value={productForm.modelName}
                                  onChange={(e) => setPF("modelName", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="HP Rating"
                                  value={productForm.hpRating}
                                  onChange={(e) => setPF("hpRating", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Drive Type (optional)"
                                  value={productForm.driveType}
                                  onChange={(e) => setPF("driveType", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                >
                                  <MenuItem value="">Select</MenuItem>
                                  <MenuItem value="2WD">2WD</MenuItem>
                                  <MenuItem value="4WD">4WD</MenuItem>
                                  <MenuItem value="NA">N/A</MenuItem>
                                </TextField>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Fuel Type (optional)"
                                  value={productForm.fuelType}
                                  onChange={(e) => setPF("fuelType", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                >
                                  <MenuItem value="">Select</MenuItem>
                                  <MenuItem value="Diesel">Diesel</MenuItem>
                                  <MenuItem value="Petrol">Petrol</MenuItem>
                                  <MenuItem value="Electric">Electric</MenuItem>
                                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                                </TextField>
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  select
                                  fullWidth
                                  required
                                  size="small"
                                  label="Govt Subsidy Item *"
                                  value={productForm.subsidyItemId}
                                  onChange={(e) => setPF("subsidyItemId", e.target.value)}
                                  error={!!productErrors.subsidyItemId}
                                  helperText={productErrors.subsidyItemId ? productErrors.subsidyItemId : " "}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                >
                                  <MenuItem value="">Select Subsidy Item</MenuItem>
                                  {subsidyOptions.map((it) => (
                                    <MenuItem key={it.id} value={it.id}>
                                      {it.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                {!productErrors.subsidyItemId ? (
                                  <Typography sx={{ mt: 0.6, fontSize: 11, color: UI.muted }}>
                                    *Required. Without this, product will not save.
                                  </Typography>
                                ) : null}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Warranty (optional)"
                                  value={productForm.warranty}
                                  onChange={(e) => setPF("warranty", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Service Support (optional)"
                                  value={productForm.serviceSupport}
                                  onChange={(e) => setPF("serviceSupport", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </FormBlock>
                        )}

                        {/* C */}
                        <FormBlock code="C" title="Pricing" sub="Enter price values. On-road auto-calculates (machinery).">
                          <Grid container spacing={2}>
                            {!isMachinery ? (
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Price (₹)"
                                  value={productForm.price}
                                  onChange={(e) => setPF("price", e.target.value)}
                                  sx={{ bgcolor: "white", borderRadius: 2 }}
                                />
                              </Grid>
                            ) : (
                              <>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Ex-Showroom (₹)"
                                    value={productForm.priceExShowroom}
                                    onChange={(e) => setPF("priceExShowroom", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="RTO Tax (₹)"
                                    value={productForm.rtoTax}
                                    onChange={(e) => setPF("rtoTax", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Insurance (₹)"
                                    value={productForm.insuranceAmount}
                                    onChange={(e) => setPF("insuranceAmount", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Accessories (₹)"
                                    value={productForm.accessoriesAmount}
                                    onChange={(e) => setPF("accessoriesAmount", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Other Charges (₹)"
                                    value={productForm.otherCharges}
                                    onChange={(e) => setPF("otherCharges", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="On-Road (Auto)"
                                    value={String(computedOnRoad || 0)}
                                    InputProps={{ readOnly: true }}
                                    sx={{ bgcolor: "rgba(15,23,42,0.04)", borderRadius: 2 }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="EMI Options (optional)"
                                    value={productForm.emiOptions}
                                    onChange={(e) => setPF("emiOptions", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Subsidy Scheme Tag (optional)"
                                    value={productForm.subsidySchemeTag}
                                    onChange={(e) => setPF("subsidySchemeTag", e.target.value)}
                                    sx={{ bgcolor: "white", borderRadius: 2 }}
                                  />
                                </Grid>
                              </>
                            )}
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: "wrap" }}>
                            {editingProductId ? (
                              <Button
                                type="button"
                                onClick={() => resetProductForm(false)}
                                variant="contained"
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 950,
                                  bgcolor: "rgba(15,23,42,0.06)",
                                  color: UI.text,
                                  "&:hover": { bgcolor: "rgba(15,23,42,0.10)" },
                                }}
                              >
                                Cancel Edit
                              </Button>
                            ) : null}

                            <Button
                              type="submit"
                              disabled={savingProduct}
                              variant="contained"
                              sx={{
                                borderRadius: 999,
                                fontWeight: 950,
                                bgcolor: "rgba(109,40,217,0.92)",
                                "&:hover": { bgcolor: "rgba(109,40,217,1)" },
                                minWidth: 240,
                              }}
                            >
                              {savingProduct ? "Saving…" : editingProductId ? "Update Product" : "Save Product (Pending)"}
                            </Button>
                          </Stack>
                        </FormBlock>
                      </Stack>
                    </Grid>

                    {/* RIGHT SIDE SUMMARY PANEL (more professional) */}
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ ...premiumPaperSx, p: 2.2 }}>
                        <Typography sx={{ fontWeight: 950, color: UI.text }}>Quick Summary</Typography>
                        <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.4 }}>
                          Required fields must be filled. Save will be <b>Pending</b> for admin approval.
                        </Typography>

                        <Divider sx={{ my: 1.6 }} />

                        <Stack spacing={1}>
                          <Chip
                            label={`Component: ${productForm.category ? (COMPONENT_LABELS[toComponentId(productForm.category)] || productForm.category) : "—"}`}
                            sx={{ justifyContent: "flex-start", fontWeight: 900 }}
                          />
                          {isMachinery ? (
                            <>
                              <Chip
                                label={`Machinery Group: ${getMachGroupLabel(productForm.machGroup || "TRACTORS")}`}
                                sx={{ justifyContent: "flex-start", fontWeight: 900 }}
                              />
                              <Chip
                                label={`Subsidy: ${productForm.subsidyItemId ? getSubsidyLabel(productForm.machGroup, productForm.subsidyItemId) : "—"}`}
                                sx={{ justifyContent: "flex-start", fontWeight: 900 }}
                              />
                              <Chip
                                label={`On-road (Auto): ₹${Number(computedOnRoad || 0).toLocaleString("en-IN")}`}
                                sx={{ justifyContent: "flex-start", fontWeight: 950, bgcolor: "rgba(5,150,105,0.10)", color: "#065f46" }}
                              />
                            </>
                          ) : (
                            <Chip
                              label={`Price: ${productForm.price ? `₹${Number(calcNum(productForm.price)).toLocaleString("en-IN")}` : "—"}`}
                              sx={{ justifyContent: "flex-start", fontWeight: 950, bgcolor: "rgba(79,70,229,0.10)", color: "#3730a3" }}
                            />
                          )}
                        </Stack>

                        <Divider sx={{ my: 1.6 }} />

                        <Typography sx={{ fontSize: 12, color: UI.muted }}>
                          Tip: For fastest approvals, ensure product details are complete and accurate.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Section>
            )}

            {/* PRODUCT CATALOGUE */}
            {activeTab === "products" && (
              <Section title="Product Catalogue" sub="Edit / Delete products.">
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={productStatusFilter}
                    onChange={(e) => setProductStatusFilter(e.target.value)}
                    sx={{ bgcolor: "rgba(255,255,255,0.85)", borderRadius: 2, minWidth: 150 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Component"
                    value={productCategoryFilter}
                    onChange={(e) => setProductCategoryFilter(e.target.value)}
                    sx={{ bgcolor: "rgba(255,255,255,0.85)", borderRadius: 2, minWidth: 200 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {categoryOptions.map((id) => (
                      <MenuItem key={id} value={id}>
                        {COMPONENT_LABELS[id] || id}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    size="small"
                    label="Search"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    sx={{ bgcolor: "rgba(255,255,255,0.85)", borderRadius: 2, minWidth: 260 }}
                  />
                </Stack>

                <TableContainer sx={{ border: "2px solid #0f172a", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
                  <Table size="small" sx={{ "& td, & th": { border: "1px solid #0f172a" } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                        <TableCell sx={{ fontWeight: 950 }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 950 }}>Component</TableCell>
                        <TableCell sx={{ fontWeight: 950, width: 140 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 950, width: 220 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredProducts.map((p) => {
                        const compId = p.componentId || toComponentId(p.category);
                        const st = (p.status || "pending").toLowerCase();
                        return (
                          <TableRow key={p.id} hover>
                            <TableCell sx={{ fontWeight: 800 }}>{p.title || p.name}</TableCell>
                            <TableCell sx={{ color: "rgba(15,23,42,0.75)" }}>{COMPONENT_LABELS[compId] || compId}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={p.status || "pending"}
                                sx={{
                                  fontWeight: 950,
                                  bgcolor:
                                    st === "approved"
                                      ? "rgba(5,150,105,0.14)"
                                      : st === "rejected"
                                      ? "rgba(225,29,72,0.14)"
                                      : "rgba(217,119,6,0.14)",
                                  color: st === "approved" ? "#065f46" : st === "rejected" ? "#9f1239" : "#92400e",
                                  border: "1px solid #0f172a",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleEditProduct(p)}
                                  sx={{ borderRadius: 2, fontWeight: 950, bgcolor: "rgba(79,70,229,0.92)" }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleDeleteProduct(p)}
                                  sx={{ borderRadius: 2, fontWeight: 950, bgcolor: "rgba(225,29,72,0.92)" }}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Section>
            )}

            {/* FARMER REQUESTS */}
            {activeTab === "applications" && (
              <Section title="Farmer Quotation Requests" sub="Generate quotation on your letterhead (approval required).">
                <Box sx={{ position: "sticky", top: 0, zIndex: 20, bgcolor: "#fff", border: "2px solid #0f172a", borderRadius: 2, p: 1.2, mb: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <TextField select size="small" label="Status" value={appStatusFilter} onChange={(e) => setAppStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="submitted">Submitted</MenuItem>
                      <MenuItem value="processed">Processed</MenuItem>
                    </TextField>

                    <TextField size="small" label="Search" value={appSearch} onChange={(e) => setAppSearch(e.target.value)} sx={{ minWidth: 300 }} />

                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={downloadApplicationsCsv}
                      disabled={!applications.length}
                      variant="contained"
                      sx={{ fontWeight: 950, borderRadius: 2, bgcolor: "#0f172a", border: "2px solid #0f172a", "&:hover": { bgcolor: "#020617" } }}
                    >
                      Download CSV
                    </Button>
                  </Stack>
                </Box>

                {appsLoading ? (
                  <Typography sx={{ fontSize: 13, color: UI.muted }}>Loading applications…</Typography>
                ) : filteredApps.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: UI.muted }}>No applications found.</Typography>
                ) : (
                  <TableContainer sx={{ border: "2px solid #0f172a", borderRadius: 2, overflow: "auto", bgcolor: "#fff", maxHeight: "520px" }}>
                    <Table stickyHeader size="small" sx={{ "& td, & th": { border: "1px solid #0f172a" } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                          {["#", "Farmer Details", "Machine Details", "Status", "Actions"].map((h, i) => (
                            <TableCell key={i} sx={{ fontWeight: 950, bgcolor: "#f1f5f9", position: "sticky", top: 0, zIndex: 2 }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredApps.map((a, idx) => {
                          const status = (a.status || "submitted").toLowerCase();
                          return (
                            <TableRow key={a.id} hover>
                              <TableCell sx={{ fontWeight: 900 }}>{idx + 1}</TableCell>

                              <TableCell>
                                <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>{(a.farmerName || "-").toUpperCase()}</Typography>
                                <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.75)", mt: 0.3 }}>
                                  Membership: <b>{a.memberId || "-"}</b> • Mobile: <b>{a.farmerMobile || "-"}</b>
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.75)", mt: 0.2 }}>
                                  Area: {a.farmerDistrict || "-"} / {a.farmerTaluka || "-"} {a.farmerVillage ? ` / ${a.farmerVillage}` : ""}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                                  {a.companyBrand || a.brand || "-"} {a.modelName || "-"} {a.hpRating ? `(${a.hpRating})` : ""}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.75)", mt: 0.3 }}>
                                  Group: <b>{a.machGroup ? getMachGroupLabel(a.machGroup) : "-"}</b>
                                </Typography>
                                {a.subsidyItemId ? (
                                  <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.75)", mt: 0.2 }}>
                                    Subsidy: <b>{getSubsidyLabel(a.machGroup, a.subsidyItemId)}</b>
                                  </Typography>
                                ) : null}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={a.status || "submitted"}
                                  sx={{
                                    fontWeight: 950,
                                    bgcolor: status === "processed" ? "rgba(5,150,105,0.14)" : "rgba(79,70,229,0.14)",
                                    color: status === "processed" ? "#065f46" : "#3730a3",
                                    border: "1px solid #0f172a",
                                  }}
                                />
                                {a.quotationGenerated ? (
                                  <Chip
                                    size="small"
                                    label={`Quotation: ${a.quotationNo || "-"}`}
                                    sx={{ mt: 1, fontWeight: 950, bgcolor: "rgba(109,40,217,0.14)", color: "#4c1d95", border: "1px solid #0f172a" }}
                                  />
                                ) : null}
                              </TableCell>

                              <TableCell>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                  <Button
                                    size="small"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => openQuotation(a)}
                                    variant="contained"
                                    sx={{ fontWeight: 950, borderRadius: 2, bgcolor: "rgba(15,23,42,0.08)", color: "#0f172a", border: "1px solid #0f172a" }}
                                  >
                                    Generate Quotation
                                  </Button>

                                  <Button
                                    size="small"
                                    onClick={() => handleMarkApplicationProcessed(a)}
                                    disabled={status === "processed"}
                                    variant="contained"
                                    sx={{
                                      fontWeight: 950,
                                      borderRadius: 2,
                                      bgcolor: status === "processed" ? "rgba(15,23,42,0.06)" : "rgba(5,150,105,0.92)",
                                      color: status === "processed" ? "rgba(15,23,42,0.55)" : "#fff",
                                      border: "1px solid #0f172a",
                                    }}
                                  >
                                    {status === "processed" ? "Confirmed" : "Mark Confirmed"}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Section>
            )}

            {/* REPORTS */}
            {activeTab === "reports" && (
              <Section title="Reports & CSV Export" sub="Export your catalogue + farmer requests for offline sharing.">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, ...premiumPaperSx }}>
                      <Typography sx={{ fontWeight: 950, color: UI.text }}>📦 Product Catalogue</Typography>
                      <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.5 }}>
                        Export products with group/company/model/status.
                      </Typography>
                      <Button
                        startIcon={<DownloadIcon />}
                        onClick={downloadProductsCsv}
                        disabled={!products.length}
                        variant="contained"
                        sx={{ mt: 2, borderRadius: 999, fontWeight: 950, bgcolor: "rgba(79,70,229,0.92)", "&:hover": { bgcolor: "rgba(79,70,229,1)" } }}
                      >
                        Download Products CSV
                      </Button>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, ...premiumPaperSx }}>
                      <Typography sx={{ fontWeight: 950, color: UI.text }}>👨‍🌾 Farmer Requests</Typography>
                      <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.5 }}>
                        Export applications with company/model/subsidy details.
                      </Typography>
                      <Button
                        startIcon={<DownloadIcon />}
                        onClick={downloadApplicationsCsv}
                        disabled={!applications.length}
                        variant="contained"
                        sx={{ mt: 2, borderRadius: 999, fontWeight: 950, bgcolor: "rgba(5,150,105,0.92)", "&:hover": { bgcolor: "rgba(5,150,105,1)" } }}
                      >
                        Download Applications CSV
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>
              </Section>
            )}
          </Stack>

          {/* QUOTATION MODAL */}
          <Dialog open={quoteOpen && !!quoteApp} onClose={closeQuotation} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 950, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Generate Quotation
              <IconButton onClick={closeQuotation}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              {quoteApp ? (
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.70)" }}>
                    Farmer: <b>{quoteApp.farmerName || "-"}</b> • Machine:{" "}
                    <b>{(quoteApp.companyBrand || quoteApp.brand || "-")} {quoteApp.modelName || "-"}</b>
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Quotation No *"
                        value={quoteForm.quotationNo}
                        onChange={(e) => setQuoteForm((p) => ({ ...p, quotationNo: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Quotation Date *"
                        InputLabelProps={{ shrink: true }}
                        value={quoteForm.quotationDate}
                        onChange={(e) => setQuoteForm((p) => ({ ...p, quotationDate: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Typography sx={{ fontSize: 12, color: "rgba(15,23,42,0.70)" }}>
                    Letterhead:{" "}
                    <b style={{ color: vendor?.letterhead_url ? UI.ok : UI.danger }}>
                      {vendor?.letterhead_url ? "Uploaded" : "Not uploaded"}
                    </b>
                  </Typography>
                </Stack>
              ) : null}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => generateQuotationPDF({ saveToFirestore: false })}
                startIcon={<DownloadIcon />}
                disabled={quoteSaving}
                variant="outlined"
              >
                {quoteSaving ? "Working…" : "Download PDF Only"}
              </Button>

              <Button
                onClick={() => generateQuotationPDF({ saveToFirestore: true })}
                startIcon={<PictureAsPdfIcon />}
                disabled={quoteSaving}
                variant="contained"
                sx={{ fontWeight: 950 }}
              >
                {quoteSaving ? "Generating…" : "Save + Download"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
}
